import React from "react";

const ScreenshotViewer = ({ screenshot, rect, strategy, onClose }) => {
  if (!screenshot || !rect) return null;

  const { data, width: imgWidth, height: imgHeight } = screenshot.screenshot;

  // Check for scroll offset in screenshot data
  const scrollY = screenshot.scrollY || screenshot.windowScrollY || 0;
  const scrollX = screenshot.scrollX || screenshot.windowScrollX || 0;

  // Adjust rect for scroll offset if this is a viewport screenshot
  const adjustedRect = {
    ...rect,
    top: rect.top - scrollY,
    bottom: rect.bottom - scrollY,
    left: rect.left - scrollX,
    right: rect.right - scrollX,
  };

  const PADDING = 300; // Increased padding to provide more context

  // Calculate the dimensions of the area to be shown
  const cropX = Math.max(0, adjustedRect.left - PADDING);
  const cropY = Math.max(0, adjustedRect.top - PADDING);
  const cropWidth = Math.min(
    imgWidth - cropX,
    adjustedRect.width + PADDING * 2
  );
  const cropHeight = Math.min(
    imgHeight - cropY,
    adjustedRect.height + PADDING * 2
  );

  // Determine the scale factor to fit the cropped area within 90% of the viewport
  const viewportWidth = window.innerWidth * 0.9;
  const viewportHeight = window.innerHeight * 0.9;
  const scale = Math.min(
    viewportWidth / cropWidth,
    viewportHeight / cropHeight,
    1
  );

  // This outer container will be the final size of the modal content
  const modalContentStyle = {
    width: cropWidth * scale,
    height: cropHeight * scale,
    position: "relative",
    boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
    borderRadius: "0.5rem",
  };

  // This container will be the full size of the crop area and will be scaled down
  const scalerStyle = {
    width: cropWidth,
    height: cropHeight,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
  };

  // This container creates the "crop window"
  const cropWindowStyle = {
    position: "relative",
    width: cropWidth,
    height: cropHeight,
    overflow: "hidden",
    background: "#f0f0f0",
  };

  // The full screenshot, translated to show the correct part
  const fullImageStyle = {
    position: "absolute",
    width: imgWidth,
    height: imgHeight,
    maxWidth: "none",
    transform: `translate(-${cropX}px, -${cropY}px)`,
  };

  const isDesktopData = strategy === "desktop";

  // Use percentage-based offset instead of fixed pixels
  // Calculate offset as a percentage of the element width
  const PERCENTAGE_OFFSET = isDesktopData ? 0.08 : 0; // 8% of element width for desktop
  const HORIZONTAL_OFFSET = rect.width * PERCENTAGE_OFFSET;

  // The highlight box, with coordinates relative to the crop window
  const highlightStyle = {
    position: "absolute",
    top: adjustedRect.top - cropY,
    left: adjustedRect.left - cropX - HORIZONTAL_OFFSET,
    width: adjustedRect.width,
    height: adjustedRect.height,
    border: "3px solid red",
    boxSizing: "border-box",
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={scalerStyle}>
          <div style={cropWindowStyle}>
            <img
              src={data}
              alt="Contextual screenshot of the failing element"
              style={fullImageStyle}
            />
            {/* Top overlay - darkens everything above the highlighted element */}
            <div
              className="absolute"
              style={{
                width: imgWidth,
                height: adjustedRect.top,
                transform: `translate(-${cropX}px, -${cropY}px)`,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
            {/* Left overlay - darkens everything to the left of the highlighted element */}
            <div
              className="absolute"
              style={{
                width: adjustedRect.left - HORIZONTAL_OFFSET,
                height: adjustedRect.height,
                top: adjustedRect.top - cropY,
                left: -cropX,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
            {/* Right overlay - darkens everything to the right of the highlighted element */}
            <div
              className="absolute"
              style={{
                width:
                  imgWidth -
                  (adjustedRect.left - HORIZONTAL_OFFSET) -
                  adjustedRect.width,
                height: adjustedRect.height,
                top: adjustedRect.top - cropY,
                left:
                  adjustedRect.left -
                  cropX -
                  HORIZONTAL_OFFSET +
                  adjustedRect.width,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
            {/* Bottom overlay - darkens everything below the highlighted element */}
            <div
              className="absolute"
              style={{
                width: imgWidth,
                height: imgHeight - adjustedRect.top - adjustedRect.height,
                top: adjustedRect.top - cropY + adjustedRect.height,
                left: -cropX,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
            <div style={highlightStyle} />
          </div>
        </div>
        <button
          onClick={onClose}
          className="absolute top-1 right-1 text-foreground bg-card/80 rounded-full p-1 leading-none z-10 hover:text-foreground hover:bg-card"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ScreenshotViewer;
